import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// A single shared audio element drives both the fixed mini-player and the
// inline play state on the card that originated playback. Only one story
// plays at a time — starting a second swaps the first out.

export const AudioPlayerContext = createContext(null);

export function AudioPlayerProvider({ children }) {
  // The single <audio> element is created once and lives for the app's
  // lifetime. Keeping it in a ref means playback survives re-renders
  // without interruption. Lazily initialised in an effect so we never
  // touch the ref during render.
  const audioRef = useRef(null);

  const [current, setCurrent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (audioRef.current === null && typeof Audio !== 'undefined') {
      audioRef.current = new Audio();
      audioRef.current.preload = 'metadata';
    }
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setElapsed(0);
    };
    const onLoadedMetadata = (event) => setDuration(event.target.duration || 0);
    const onTimeUpdate = (event) => setElapsed(event.target.currentTime || 0);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, []);

  // play starts a story. If the same story is already loaded, it toggles
  // play/pause instead of restarting — matching the card's play button.
  const play = useCallback((story) => {
    const audio = audioRef.current;
    if (!audio) return;

    if (current && current.id === story.id) {
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
      return;
    }

    audio.src = story.articleAudioUrl || story.audioUrl;
    audio.play().catch(() => {
      // Autoplay can be blocked; the user's tap is a user gesture so this
      // is rare, but swallow the rejection rather than crashing.
    });
    setCurrent(story);
    setElapsed(0);
    setDuration(0);
  }, [current]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    audioRef.current?.play().catch(() => {});
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    setCurrent(null);
    setIsPlaying(false);
    setElapsed(0);
    setDuration(0);
  }, []);

  const seek = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(seconds)) return;
    audio.currentTime = seconds;
    setElapsed(seconds);
  }, []);

  const value = {
    current,
    isPlaying,
    duration,
    elapsed,
    play,
    pause,
    resume,
    stop,
    seek,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (context === null) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
}